// ===================== SubscriptionPlansPage.jsx =====================
import React, { useEffect, useState } from "react";
import { useLocation, useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  getAllActiveSubscriptions,
  getUserSubscriptions,
} from "../../features/mobsubscriptionSlice";
import instance from "../../utils/api_instance.util";

const looksLikeJwt = (value) => {
  const s = String(value || "");
  return s.split(".").length === 3 && s.startsWith("eyJ");
};

const SubscriptionPlansPage = () => {
  const location = useLocation();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { isLoading, plans, currentSubscription } = useSelector(
    (state) => state.mobsubscription
  );

  const [billingCycle, setBillingCycle] = useState("monthly"); // monthly | yearly
  const [viewMode, setViewMode] = useState("list"); // list | details
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [authError, setAuthError] = useState("");
  const [bootstrapping, setBootstrapping] = useState(true);

  const params = useParams();
  const handoffCode = params?.code || "";
  const legacyToken = params?.token || "";
  const legacyUserId = params?.userid || "";

  const tokenFromQuery =
    new URLSearchParams(location.search).get("token") || "";
  const tokenFromSession = sessionStorage.getItem("checkout_web_token") || "";

  const [token, setToken] = useState(tokenFromSession);
  const [userid, setUserid] = useState("");

  useEffect(() => {
    let cancelled = false;

    const bootstrap = async () => {
      setAuthError("");

      // Refuse reusable JWTs in the URL path or query (legacy attack surface).
      if (looksLikeJwt(legacyToken) || looksLikeJwt(tokenFromQuery)) {
        if (!cancelled) {
          setAuthError(
            "This subscription link is no longer valid. Return to the app and open Manage Subscription again."
          );
          setBootstrapping(false);
          sessionStorage.removeItem("token");
          sessionStorage.removeItem("checkout_web_token");
        }
        return;
      }

      if (handoffCode) {
        if (looksLikeJwt(handoffCode)) {
          if (!cancelled) {
            setAuthError("Invalid checkout link.");
            setBootstrapping(false);
          }
          return;
        }
        try {
          const { data } = await instance.post(
            "/api/v1/app/auth/checkout-handoff/redeem",
            { handoff: handoffCode }
          );
          const redeemed = data?.result || {};
          const webToken = redeemed.token;
          const userId = redeemed.user_id;
          if (!webToken) {
            throw new Error(data?.message || "Handoff redemption failed");
          }
          if (!cancelled) {
            sessionStorage.setItem("checkout_web_token", String(webToken));
            // Clear any leftover full-session token from older builds.
            sessionStorage.removeItem("token");
            setToken(String(webToken));
            if (userId != null) setUserid(String(userId));
            // Drop the handoff code from the address bar without a reload.
            navigate("/subscriptions", { replace: true });
          }
        } catch (err) {
          const msg =
            err?.response?.data?.message ||
            err?.message ||
            "Checkout link expired or already used.";
          if (!cancelled) {
            setAuthError(msg);
            setBootstrapping(false);
          }
          return;
        }
      } else if (tokenFromSession) {
        if (!cancelled) setToken(tokenFromSession);
      } else if (legacyToken && !looksLikeJwt(legacyToken)) {
        // Opaque legacy path segment that is not a JWT — treat as handoff code.
        try {
          const { data } = await instance.post(
            "/api/v1/app/auth/checkout-handoff/redeem",
            { handoff: legacyToken }
          );
          const redeemed = data?.result || {};
          if (!cancelled && redeemed.token) {
            sessionStorage.setItem("checkout_web_token", String(redeemed.token));
            sessionStorage.removeItem("token");
            setToken(String(redeemed.token));
            if (redeemed.user_id != null) setUserid(String(redeemed.user_id));
            if (legacyUserId) setUserid(String(legacyUserId));
          }
        } catch (err) {
          if (!cancelled) {
            setAuthError(
              err?.response?.data?.message ||
                "This subscription link is no longer valid."
            );
            setBootstrapping(false);
          }
          return;
        }
      } else if (!tokenFromSession) {
        if (!cancelled) {
          setAuthError(
            "Open Manage Subscription from the Scavengertime app to continue."
          );
          setBootstrapping(false);
        }
        return;
      }

      if (!cancelled) setBootstrapping(false);
    };

    bootstrap();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handoffCode, legacyToken, legacyUserId, tokenFromQuery]);

  useEffect(() => {
    if (token) dispatch(getAllActiveSubscriptions({ token }));
  }, [token, dispatch]);

  useEffect(() => {
    if (token && userid) {
      dispatch(getUserSubscriptions({ token, user_id: userid }));
    }
  }, [token, userid, dispatch]);

  const subCheck = currentSubscription;
  const isCurrentPlan = (plan) => {
    if (!subCheck) return false;
    const selectedType = billingCycle === "monthly" ? "M" : "Y";
    const planType = subCheck?.planType ?? subCheck?.plan_type ?? null;
    return (
      plan?.id === subCheck?.id &&
      (planType === null || planType === selectedType)
    );
  };

  const getPriceText = (plan) => {
    const price =
      billingCycle === "monthly" ? plan?.price_monthly : plan?.price_annually;
    if (price === null || price === undefined || price === "") return "Free";
    return `$${Number(price).toFixed(2)}`;
  };

  const getPriceSuffix = () =>
    billingCycle === "monthly" ? "/ month" : "/ year";

  const openDetails = (plan) => {
    setSelectedPlan(plan);
    setViewMode("details");
  };

  const goBackToList = () => {
    setSelectedPlan(null);
    setViewMode("list");
  };

  const getPlanBgImage = (plan) => {
    if (plan?.name === "Ranger") return "/assets/img/ranger.jpg";
    if (plan?.name === "Legend") return "/assets/img/legend.jpg";
    return "/assets/img/explorer.png";
  };

  const renderFeatureRow = (label, value) => {
    if (value === null || value === undefined) return null;
    return (
      <li className="mb-2">
        <strong>{label} :</strong> {value}
      </li>
    );
  };

  if (bootstrapping) {
    return (
      <main className="subscription-page">
        <div className="container py-5 text-center">
          <div className="spinner-border" role="status" />
          <div className="mt-2">Preparing secure checkout…</div>
        </div>
      </main>
    );
  }

  if (authError) {
    return (
      <main className="subscription-page">
        <div className="container py-5">
          <div className="alert alert-warning" role="alert">
            {authError}
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className="subscription-page">
      <div className="container py-3">
        {viewMode === "details" ? (
          <div className="d-flex align-items-center mb-3">
            <button className="btn btn-link p-0 me-2" onClick={goBackToList}>
              ←
            </button>
            <h5 className="m-0 fw-bold">Subscription Details</h5>
          </div>
        ) : (
          <div className="mb-3">
            <h5 className="fw-bold mb-1">
              Choose the plan that's right for you
            </h5>
            <div className="text-muted small">
              Join Scavengertime and select from our flexible subscription
              options.
            </div>
          </div>
        )}

        <div className="segmented-toggle mb-3">
          <button
            className={`seg-btn ${billingCycle === "monthly" ? "active" : ""}`}
            onClick={() => setBillingCycle("monthly")}
            type="button"
          >
            Monthly
          </button>
          <button
            className={`seg-btn ${billingCycle === "yearly" ? "active" : ""}`}
            onClick={() => setBillingCycle("yearly")}
            type="button"
          >
            Yearly
          </button>
        </div>

        {isLoading ? (
          <div className="text-center py-5">
            <div className="spinner-border" role="status" />
            <div className="mt-2">Loading plans...</div>
          </div>
        ) : null}

        {viewMode === "list" && !isLoading ? (
          <div className="plans-list">
            {(plans || []).map((plan) => (
              <div
                key={plan.id}
                className="plan-card mb-3"
                style={{ backgroundImage: `url(${getPlanBgImage(plan)})` }}
              >
                <div className="plan-card-overlay">
                  <div>
                    <div className="plan-name">{plan.name}</div>
                    <div className="plan-price">
                      {getPriceText(plan)}{" "}
                      <span className="plan-suffix">{getPriceSuffix()}</span>
                    </div>
                  </div>
                  <div className="plan-desc">
                    {(plan.description || "").slice(0, 90)}
                    {plan?.description?.length > 90 ? "..." : ""}
                  </div>
                  {!isCurrentPlan(plan) ? (
                    <div className="d-flex gap-2 mt-3">
                      <button
                        className="btn btn-primary btn-sm flex-grow-1"
                        type="button"
                        onClick={() => openDetails(plan)}
                      >
                        Plan Details →
                      </button>
                    </div>
                  ) : null}
                </div>
              </div>
            ))}
          </div>
        ) : null}

        {viewMode === "details" && selectedPlan ? (
          <div
            className="details-card"
            style={{ backgroundImage: `url(${getPlanBgImage(selectedPlan)})` }}
          >
            <div className="details-overlay">
              <div className="text-center mb-3">
                <div className="details-title">{selectedPlan.name}</div>
                <div className="details-price">
                  {getPriceText(selectedPlan)}{" "}
                  <span className="details-suffix">{getPriceSuffix()}</span>
                </div>
              </div>
              <div className="offering-desc mb-4">
                {(selectedPlan.description || "")
                  .split("\n")
                  .map((line, idx) => (
                    <div key={idx} className="mb-2">
                      {line}
                    </div>
                  ))}
              </div>
              <h6 className="fw-bold mb-3">Features</h6>
              <ul className="feature-list">
                {renderFeatureRow("Max Saved Hunts", selectedPlan.max_hunts)}
                {renderFeatureRow(
                  "Max Saved Quests",
                  selectedPlan.max_challenges
                )}
                {renderFeatureRow("Max Quest Share", selectedPlan.max_share)}
                {renderFeatureRow(
                  "Max Hunt Challenges",
                  selectedPlan.max_hunt_items
                )}
                {renderFeatureRow(
                  "Max Quest Challenges",
                  selectedPlan.max_challenge_items
                )}
                {renderFeatureRow(
                  "Max Created Challenges in Library",
                  selectedPlan.max_created_items
                )}
                {renderFeatureRow(
                  "Max Purchased Challenges in Library",
                  selectedPlan.max_purchased_items === null
                    ? "Unlimited"
                    : selectedPlan.max_purchased_items
                )}
                {renderFeatureRow(
                  "Max Hunt Teams",
                  selectedPlan.max_hunt_teams
                )}
                {renderFeatureRow(
                  "Video Available (days)",
                  selectedPlan.video_available
                )}
                {renderFeatureRow(
                  "Relaunch",
                  selectedPlan.is_relaunch ? "Yes" : "No"
                )}
                {renderFeatureRow(
                  "Relaunch Quests",
                  selectedPlan.is_relaunch_quests ? "Yes" : "No"
                )}
                {renderFeatureRow("Ads", selectedPlan.is_ads ? "Yes" : "No")}
              </ul>
            </div>
          </div>
        ) : null}
      </div>

      <style>{`
        .subscription-page { background: #f5f6f8; min-height: 100vh; }
        .segmented-toggle { display:flex; gap:8px; background:#fff; padding:6px; border-radius:12px; width:100%; max-width:360px; }
        .seg-btn { flex:1; border:0; background:transparent; padding:10px 12px; border-radius:10px; font-weight:600; }
        .seg-btn.active { background:#0d2bd6; color:#fff; }
        .plan-card { border-radius:16px; background-size:cover; background-position:center; overflow:hidden; min-height:140px; }
        .plan-card-overlay { padding:16px; color:#fff; background:linear-gradient(90deg, rgba(0,0,0,0.35), rgba(0,0,0,0.05)); }
        .plan-name { font-size:18px; font-weight:800; }
        .plan-price { font-size:24px; font-weight:900; margin-top:6px; }
        .plan-suffix { font-size:14px; font-weight:700; opacity:0.9; }
        .plan-desc { font-size:13px; margin-top:10px; opacity:0.95; max-width:280px; }
        .details-card { border-radius:18px; background-size:cover; background-position:center; overflow:hidden; min-height:calc(100vh - 170px); }
        .details-overlay { padding:18px; color:#fff; background:linear-gradient(180deg, rgba(0,0,0,0.25), rgba(0,0,0,0.65)); min-height:calc(100vh - 170px); }
        .details-title { font-size:22px; font-weight:900; }
        .details-price { font-size:36px; font-weight:900; margin-top:10px; }
        .details-suffix { font-size:16px; font-weight:700; opacity:0.95; }
        .offering-desc { font-size:14px; opacity:0.98; line-height:1.35; }
        .feature-list { padding-left:18px; }
        .feature-list li { font-size:14px; }
        .btn-link { color:#000; }
        @media (min-width: 992px) {
          .plans-list { max-width:520px; margin:0 auto; }
          .details-card { max-width:520px; margin:0 auto; }
        }
      `}</style>
    </main>
  );
};

export default SubscriptionPlansPage;
